// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef } from 'react';

import { t } from '@/i18n';
import { normalizeHoleEntry, normalizeHoleEvent, parseHandicapValue } from '@/features/hole/utils';
import { appendClaimQuery } from '@/services/claimHttp';
import { applyPendingToSnapshot, loadOfflineQueue } from '@/services/offlineQueue';
import { getOfflineSubjectKey } from '@/services/offlineSubject';
import { patchRoundCache, patchUserCache, readRoundCache, readUserCache } from '@/services/roundCache';
import { DEFAULT_HOLES_COUNT, normalizeHolesCount } from '@/utils/holesCount';
import type { HoleEntry, HoleEventEntry, Player, RoundSubgameEntry, Subgame, TeamSummary } from '@/features/hole/types';

type SessionUser = {
  id: string;
  token?: string | null;
};

type UseHoleDataParams = {
  apiBaseUrl: string;
  user: SessionUser | null;
  allowGuestClaimMode: boolean;
  /** Invite viewer's token. Present => fetch over REST with ?inviteToken= instead of skipping. */
  inviteToken?: string | null;
  claimPlayerId?: string | null;
  roundId: string | null;
  roundIdIsValid: boolean;
  isNavigationReady: boolean;
  setLoading: (value: boolean) => void;
  setError: (value: string | null) => void;
  setPlayers: (players: Player[]) => void;
  setTourId: (tourId: string) => void;
  setTeams: (teams: TeamSummary[]) => void;
  setLoggedInPlayerId: (value: string | null) => void;
  setAllHoleEntries: (entries: HoleEntry[]) => void;
  gameDefinitionIdsFromParamsCount: number;
  setSelectedGameDefinitionIds: (ids: string[]) => void;
  setHolesCount: (count: number) => void;
  setStartHole?: (startHole: number | null) => void;
  setRoundSubgames: (entries: RoundSubgameEntry[]) => void;
  setHoleEvents: (events: HoleEventEntry[]) => void;
  setSubgames: (subgames: Subgame[]) => void;
  logWithContext: (event: string, payload: Record<string, unknown>, level?: 'debug' | 'warn' | 'error') => void;
};

type RawRoundGroupTeam = {
  teamId?: string;
  TeamId?: string;
  playerIds?: string[];
  PlayerIds?: string[];
};

type RawRoundGroup = {
  groupId?: string;
  GroupId?: string;
  teams?: RawRoundGroupTeam[];
  Teams?: RawRoundGroupTeam[];
};

type RoundGroupsResponse = {
  groups?: RawRoundGroup[];
  Groups?: RawRoundGroup[];
};

export type RoundPlayersSnapshot = {
  players: Player[];
  tourId?: string;
};

export type RoundMetadataSnapshot = {
  gameDefinitionIds: string[];
  holesCount: number;
  /** The hole the round started on (back-nine / shotgun starts); 1 for a normal round. */
  startHoleNumber: number;
};

const roundPlayersCache = new Map<string, RoundPlayersSnapshot>();
const roundPlayersRequests = new Map<string, Promise<RoundPlayersSnapshot>>();
const roundTourIdCache = new Map<string, string>();
const roundTourIdRequests = new Map<string, Promise<string | null>>();
const roundTeamsCache = new Map<string, TeamSummary[]>();
const roundTeamsRequests = new Map<string, Promise<TeamSummary[]>>();
const roundMetadataCache = new Map<string, RoundMetadataSnapshot>();
const roundMetadataRequests = new Map<string, Promise<RoundMetadataSnapshot>>();
const roundHoleEntriesCache = new Map<string, HoleEntry[]>();
const roundHoleEntriesRequests = new Map<string, Promise<HoleEntry[]>>();
const roundSubgamesCache = new Map<string, RoundSubgameEntry[]>();
const roundSubgamesRequests = new Map<string, Promise<RoundSubgameEntry[]>>();
const roundHoleEventsCache = new Map<string, HoleEventEntry[]>();
const roundHoleEventsRequests = new Map<string, Promise<HoleEventEntry[]>>();
const subgamesCache = new Map<string, Subgame[]>();
const subgamesRequests = new Map<string, Promise<Subgame[]>>();

const buildRoundCacheKey = (apiBaseUrl: string, roundId: string, userId: string | null | undefined) => {
  return `${apiBaseUrl}|${roundId}|${userId ?? 'anonymous'}`;
};

const buildUserCacheKey = (apiBaseUrl: string, userId: string | null | undefined) => {
  return `${apiBaseUrl}|${userId ?? 'anonymous'}`;
};

const buildRoundTeamsCacheKey = buildRoundCacheKey;

const normalizeStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter((item) => item.length > 0);
};

const clonePlayersSnapshot = (snapshot: RoundPlayersSnapshot): RoundPlayersSnapshot => ({
  players: snapshot.players.map((player) => ({ ...player })),
  tourId: snapshot.tourId,
});

const cloneMetadataSnapshot = (snapshot: RoundMetadataSnapshot): RoundMetadataSnapshot => ({
  gameDefinitionIds: [...snapshot.gameDefinitionIds],
  holesCount: snapshot.holesCount,
  startHoleNumber: snapshot.startHoleNumber,
});

const normalizeStartHoleNumber = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 18 ? parsed : 1;
};

const normalizeRoundPlayers = (data: unknown): RoundPlayersSnapshot => {
  const record =
    data !== null && typeof data === 'object' && !Array.isArray(data)
      ? (data as { players?: unknown; Players?: unknown; tourId?: unknown; TourId?: unknown })
      : {};
  const rawList = Array.isArray(data)
    ? data
    : Array.isArray(record.players)
      ? record.players
      : Array.isArray(record.Players)
        ? record.Players
        : [];
  const players = rawList.map((player) => {
    const raw = player as Player & {
      handicap?: unknown;
      Handicap?: unknown;
      hcp?: unknown;
      Hcp?: unknown;
      playingHandicap?: unknown;
      playing_handicap?: unknown;
      imageKey?: string | null;
      ImageKey?: string | null;
      imageThumbKey?: string | null;
      ImageThumbKey?: string | null;
      isAi?: boolean;
      IsAi?: boolean;
    };
    const handicap = parseHandicapValue(
      raw.handicap ?? raw.Handicap ?? raw.hcp ?? raw.Hcp ?? raw.playingHandicap ?? raw.playing_handicap
    );
    const imageKey = raw.imageKey ?? raw.ImageKey ?? null;
    const imageThumbKey = raw.imageThumbKey ?? raw.ImageThumbKey ?? null;
    const isAi = raw.isAi ?? raw.IsAi ?? false;
    return { ...raw, handicap, imageKey, imageThumbKey, isAi };
  });
  const tourId = typeof record.tourId === 'string' ? record.tourId : typeof record.TourId === 'string' ? record.TourId : undefined;
  return { players, tourId };
};

const normalizeRoundTeams = (data: RoundGroupsResponse): TeamSummary[] => {
  const groups = data.groups ?? data.Groups ?? [];
  return groups.flatMap((group, groupIndex) => {
    const teams = group.teams ?? group.Teams ?? [];
    return teams.map((team, teamIndex) => ({
      id: team.teamId ?? team.TeamId ?? `${group.groupId ?? group.GroupId ?? groupIndex}:${teamIndex}`,
      players: team.playerIds ?? team.PlayerIds ?? [],
      // The ball this team plays in. Groups arrive in play order, so the index is the number
      // players see on the course.
      ballNumber: groupIndex + 1,
    }));
  });
};

const normalizeRoundMetadata = (data: unknown): RoundMetadataSnapshot => {
  const record = data !== null && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  return {
    gameDefinitionIds: normalizeStringList(record.gameDefinitionIds ?? record.GameDefinitionIds),
    holesCount: normalizeHolesCount(record.holesCount ?? record.HolesCount),
    startHoleNumber: normalizeStartHoleNumber(record.startHoleNumber ?? record.StartHoleNumber),
  };
};

const normalizeRoundSubgames = (data: unknown): RoundSubgameEntry[] => {
  const raw = Array.isArray(data) ? data : [];
  return raw
    .map((item) => {
      const record = item as Record<string, unknown>;
      const playerId = String(record.playerId ?? record.PlayerId ?? '');
      const subgameId = String(record.subgameId ?? record.SubgameId ?? '');
      const holeRaw = record.holeNumber ?? record.HoleNumber;
      const holeNumber = typeof holeRaw === 'number' ? holeRaw : Number(holeRaw);
      if (!playerId || !subgameId || !Number.isFinite(holeNumber)) {
        return null;
      }
      return { playerId, subgameId, holeNumber } satisfies RoundSubgameEntry;
    })
    .filter((entry): entry is RoundSubgameEntry => entry !== null);
};

const normalizeHoleEvents = (data: unknown): HoleEventEntry[] => {
  const raw = Array.isArray(data) ? data : [];
  return raw
    .map((item) => normalizeHoleEvent(item as Record<string, unknown>))
    .filter((entry): entry is HoleEventEntry => entry !== null);
};

const normalizeSubgameDefinitions = (data: unknown): Subgame[] => {
  const raw = Array.isArray(data) ? data : [];
  return raw
    .map((item) => {
      const record = item as Record<string, unknown>;
      const id = String(record.id ?? record.Id ?? '').trim();
      const name = String(record.name ?? record.Name ?? '').trim();
      if (!id || !name) {
        return null;
      }
      const descriptionRaw = record.description ?? record.Description;
      const subgame: Subgame = { id, name };
      if (typeof descriptionRaw === 'string') {
        subgame.description = descriptionRaw;
      }
      return subgame;
    })
    .filter((entry): entry is Subgame => entry !== null);
};

export const cacheRoundPlayersSnapshot = (
  apiBaseUrl: string,
  roundId: string | null | undefined,
  userId: string | null | undefined,
  snapshot: RoundPlayersSnapshot
) => {
  if (!roundId) return;
  roundPlayersCache.set(buildRoundCacheKey(apiBaseUrl, roundId, userId), clonePlayersSnapshot(snapshot));
  patchRoundCache(userId, roundId, { players: snapshot.players, tourId: snapshot.tourId });
};

export const cacheRoundMetadataSnapshot = (
  apiBaseUrl: string,
  roundId: string | null | undefined,
  userId: string | null | undefined,
  snapshot: Partial<RoundMetadataSnapshot>
) => {
  if (!roundId) return;
  const cacheKey = buildRoundCacheKey(apiBaseUrl, roundId, userId);
  const previous = roundMetadataCache.get(cacheKey);
  const merged = {
    gameDefinitionIds: snapshot.gameDefinitionIds ? [...snapshot.gameDefinitionIds] : previous?.gameDefinitionIds ?? [],
    holesCount: normalizeHolesCount(snapshot.holesCount ?? previous?.holesCount ?? DEFAULT_HOLES_COUNT),
    startHoleNumber: normalizeStartHoleNumber(snapshot.startHoleNumber ?? previous?.startHoleNumber),
  };
  roundMetadataCache.set(cacheKey, merged);
  patchRoundCache(userId, roundId, { metadata: merged });
};

export const cacheRoundHoleEntriesSnapshot = (
  apiBaseUrl: string,
  roundId: string | null | undefined,
  userId: string | null | undefined,
  entries: HoleEntry[]
) => {
  if (!roundId) return;
  roundHoleEntriesCache.set(buildRoundCacheKey(apiBaseUrl, roundId, userId), [...entries]);
  patchRoundCache(userId, roundId, { holeEntries: entries });
};

export const cacheRoundHoleEventsSnapshot = (
  apiBaseUrl: string,
  roundId: string | null | undefined,
  userId: string | null | undefined,
  events: HoleEventEntry[]
) => {
  if (!roundId) return;
  roundHoleEventsCache.set(buildRoundCacheKey(apiBaseUrl, roundId, userId), [...events]);
  patchRoundCache(userId, roundId, { holeEvents: events });
};

export const useHoleData = ({
  apiBaseUrl,
  user,
  allowGuestClaimMode,
  inviteToken,
  claimPlayerId,
  roundId,
  roundIdIsValid,
  isNavigationReady,
  setLoading,
  setError,
  setPlayers,
  setTourId,
  setTeams,
  setLoggedInPlayerId,
  setAllHoleEntries,
  gameDefinitionIdsFromParamsCount,
  setSelectedGameDefinitionIds,
  setHolesCount,
  setStartHole,
  setRoundSubgames,
  setHoleEvents,
  setSubgames,
  logWithContext,
}: UseHoleDataParams) => {
  const userToken = user?.token ?? null;
  const userId = user?.id ?? null;
  // A claim guest gets its data over the websocket, so its REST fetches are skipped. An invite
  // viewer is the opposite: no websocket player payload, but every round read below authorizes
  // ?inviteToken=, so the fetches run — just credentialed by the query string rather than a bearer.
  const inviteAccess = useMemo(() => (inviteToken ? { inviteToken } : null), [inviteToken]);
  const withAccess = useCallback((url: string) => appendClaimQuery(url, inviteAccess), [inviteAccess]);
  const accessHeaders = useMemo(
    () => (userToken ? { Authorization: `Bearer ${userToken}` } : undefined),
    [userToken],
  );
  const skipRestFetches = allowGuestClaimMode && !inviteToken;
  const logWithContextRef = useRef(logWithContext);

  useEffect(() => {
    logWithContextRef.current = logWithContext;
  }, [logWithContext]);

  useEffect(() => {
    if (!isNavigationReady || !roundId) return;
    if (skipRestFetches) {
      setLoading(false);
      setError(null);
      return;
    }
    if (!userToken && !inviteToken) {
      setLoading(false);
      return;
    }

    const cacheKey = buildRoundCacheKey(apiBaseUrl, roundId, userId);
    const cachedSnapshot = roundPlayersCache.get(cacheKey);
    if (cachedSnapshot) {
      const snapshot = clonePlayersSnapshot(cachedSnapshot);
      setPlayers(snapshot.players);
      if (snapshot.tourId) {
        setTourId(snapshot.tourId);
      }
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchPlayers = async () => {
      try {
        setLoading(true);
        setError(null);
        let request = roundPlayersRequests.get(cacheKey);
        if (!request) {
          request = fetch(withAccess(`${apiBaseUrl}/api/rounds/${roundId}/players`), {
            headers: accessHeaders,
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error();
            }
            return normalizeRoundPlayers(await response.json());
          });
          roundPlayersRequests.set(cacheKey, request);
        }

        const snapshot = await request;
        cacheRoundPlayersSnapshot(apiBaseUrl, roundId, userId, snapshot);
        if (!cancelled) {
          const next = clonePlayersSnapshot(snapshot);
          setPlayers(next.players);
          if (next.tourId) {
            setTourId(next.tourId);
          }
        }
      } catch {
        // Offline fallback: render the last-known snapshot instead of an error.
        const cached = await readRoundCache(userId, roundId).catch(() => null);
        if (!cancelled && cached?.players && cached.players.length > 0) {
          setPlayers(cached.players.map((player) => ({ ...player })));
          if (cached.tourId) {
            setTourId(cached.tourId);
          }
        } else if (!cancelled && !skipRestFetches) {
          setError(t('hole.playersLoadFailed'));
        }
      } finally {
        roundPlayersRequests.delete(cacheKey);
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchPlayers();
    return () => {
      cancelled = true;
    };
  }, [skipRestFetches, withAccess, accessHeaders, inviteToken, apiBaseUrl, isNavigationReady, roundId, setError, setLoading, setPlayers, setTourId, userId, userToken]);

  // The round players payload does not carry the tour id, and neither does any
  // round.* websocket event, so a logged-in hole screen would otherwise never
  // learn which tour the round belongs to. Tour-long games (Freeze) need it to
  // subscribe to tour leaderboard state, so resolve it from the round resource.
  useEffect(() => {
    if (!isNavigationReady || !roundId || skipRestFetches || (!userToken && !inviteToken)) return;

    const cacheKey = buildRoundCacheKey(apiBaseUrl, roundId, userId);
    const known = roundPlayersCache.get(cacheKey)?.tourId ?? roundTourIdCache.get(cacheKey);
    if (known) {
      setTourId(known);
      return;
    }

    let cancelled = false;
    const resolveTourId = async () => {
      try {
        let request = roundTourIdRequests.get(cacheKey);
        if (!request) {
          request = fetch(withAccess(`${apiBaseUrl}/api/rounds/${roundId}`), {
            headers: accessHeaders,
          }).then(async (response) => {
            if (!response.ok) return null;
            const data = (await response.json()) as { tourId?: unknown; TourId?: unknown };
            const resolved = typeof data.tourId === 'string' ? data.tourId : typeof data.TourId === 'string' ? data.TourId : '';
            return resolved.trim() || null;
          });
          roundTourIdRequests.set(cacheKey, request);
        }
        const resolved = await request;
        if (resolved) {
          roundTourIdCache.set(cacheKey, resolved);
          patchRoundCache(userId, roundId, { tourId: resolved });
          if (!cancelled) {
            setTourId(resolved);
          }
        }
      } catch {
        // Offline fallback: the players effect already restores a cached tourId.
      } finally {
        roundTourIdRequests.delete(cacheKey);
      }
    };

    void resolveTourId();
    return () => {
      cancelled = true;
    };
  }, [skipRestFetches, withAccess, accessHeaders, inviteToken, apiBaseUrl, isNavigationReady, roundId, setTourId, userId, userToken]);

  useEffect(() => {
    if (!isNavigationReady || !roundIdIsValid || !roundId) return;
    if (skipRestFetches) {
      return;
    }
    if (!userToken && !inviteToken) {
      return;
    }

    const cacheKey = buildRoundTeamsCacheKey(apiBaseUrl, roundId, userId);
    const cachedTeams = roundTeamsCache.get(cacheKey);
    if (cachedTeams) {
      setTeams(cachedTeams);
      return;
    }

    const fetchTeams = async () => {
      try {
        let request = roundTeamsRequests.get(cacheKey);
        if (!request) {
          request = fetch(withAccess(`${apiBaseUrl}/api/rounds/${roundId}/groups`), {
            headers: accessHeaders,
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load groups (${response.status})`);
            }

            const data = (await response.json()) as RoundGroupsResponse;
            return normalizeRoundTeams(data);
          });
          roundTeamsRequests.set(cacheKey, request);
        }

        const mappedTeams = await request;
        roundTeamsCache.set(cacheKey, mappedTeams);
        patchRoundCache(userId, roundId, { teams: mappedTeams });

        setTeams(mappedTeams);
        logWithContextRef.current('hole.teams.loaded', {
          message: 'Loaded group-derived teams for round.',
          teamCount: mappedTeams.length,
        });
      } catch (err) {
        logWithContextRef.current(
          'hole.teams.error',
          { message: err instanceof Error ? err.message : 'Failed to load group-derived teams.' },
          'warn'
        );
        const cached = await readRoundCache(userId, roundId).catch(() => null);
        setTeams(cached?.teams ?? []);
      } finally {
        roundTeamsRequests.delete(cacheKey);
      }
    };

    void fetchTeams();
  }, [skipRestFetches, withAccess, accessHeaders, inviteToken, apiBaseUrl, isNavigationReady, roundId, roundIdIsValid, setTeams, userId, userToken]);

  useEffect(() => {
    if (!isNavigationReady || !roundIdIsValid || !roundId) {
      if (gameDefinitionIdsFromParamsCount === 0) {
        setSelectedGameDefinitionIds([]);
      }
      setHolesCount(DEFAULT_HOLES_COUNT);
      return;
    }
    if (skipRestFetches) {
      return;
    }
    if (!userToken && !inviteToken) {
      if (gameDefinitionIdsFromParamsCount === 0) {
        setSelectedGameDefinitionIds([]);
      }
      return;
    }

    const cacheKey = buildRoundCacheKey(apiBaseUrl, roundId, userId);
    const cachedSnapshot = roundMetadataCache.get(cacheKey);
    const applyMetadata = (snapshot: RoundMetadataSnapshot) => {
      if (snapshot.gameDefinitionIds.length > 0 || gameDefinitionIdsFromParamsCount === 0) {
        setSelectedGameDefinitionIds(snapshot.gameDefinitionIds);
      }
      setHolesCount(snapshot.holesCount);
      setStartHole?.(snapshot.startHoleNumber);
    };
    if (cachedSnapshot) {
      applyMetadata(cloneMetadataSnapshot(cachedSnapshot));
      return;
    }

    let cancelled = false;
    const fetchRoundMetadata = async () => {
      try {
        let request = roundMetadataRequests.get(cacheKey);
        if (!request) {
          request = fetch(withAccess(`${apiBaseUrl}/api/rounds/${roundId}`), {
            headers: accessHeaders,
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load round (${response.status})`);
            }
            return normalizeRoundMetadata(await response.json());
          });
          roundMetadataRequests.set(cacheKey, request);
        }
        const snapshot = await request;
        cacheRoundMetadataSnapshot(apiBaseUrl, roundId, userId, snapshot);
        if (!cancelled) {
          applyMetadata(snapshot);
        }
      } catch {
        const cached = await readRoundCache(userId, roundId).catch(() => null);
        if (!cancelled && cached?.metadata) {
          applyMetadata(cached.metadata);
        } else if (!cancelled && gameDefinitionIdsFromParamsCount === 0) {
          setSelectedGameDefinitionIds([]);
          setHolesCount(DEFAULT_HOLES_COUNT);
        }
      } finally {
        roundMetadataRequests.delete(cacheKey);
      }
    };

    void fetchRoundMetadata();
    return () => {
      cancelled = true;
    };
  }, [
    skipRestFetches,
    withAccess,
    accessHeaders,
    inviteToken,
    apiBaseUrl,
    gameDefinitionIdsFromParamsCount,
    isNavigationReady,
    roundId,
    roundIdIsValid,
    setHolesCount,
    setStartHole,
    setSelectedGameDefinitionIds,
    userId,
    userToken,
  ]);

  useEffect(() => {
    if (allowGuestClaimMode && claimPlayerId) {
      setLoggedInPlayerId(claimPlayerId);
      return;
    }

    if (!userId || !userToken) {
      setLoggedInPlayerId(null);
      return;
    }

    let cancelled = false;
    const fetchCurrentPlayer = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/player/by-user/${userId}`, {
          headers: { Authorization: `Bearer ${userToken}` },
        });
        if (!response.ok) {
          if (!cancelled) {
            setLoggedInPlayerId(null);
          }
          return;
        }
        const payload = (await response.json()) as { id?: string };
        patchUserCache(userId, { loggedInPlayerId: payload.id ?? null });
        if (!cancelled) {
          setLoggedInPlayerId(payload.id ?? null);
        }
      } catch {
        const cached = await readUserCache(userId).catch(() => null);
        if (!cancelled) {
          setLoggedInPlayerId(cached?.loggedInPlayerId ?? null);
        }
      }
    };

    void fetchCurrentPlayer();
    return () => {
      cancelled = true;
    };
  }, [allowGuestClaimMode, apiBaseUrl, claimPlayerId, setLoggedInPlayerId, userId, userToken]);

  useEffect(() => {
    if (!roundId || !isNavigationReady || !roundIdIsValid) {
      setRoundSubgames([]);
      return;
    }
    if (skipRestFetches) {
      setRoundSubgames([]);
      return;
    }
    if (!userToken && !inviteToken) {
      setRoundSubgames([]);
      return;
    }

    const cacheKey = buildRoundCacheKey(apiBaseUrl, roundId, userId);
    const cachedSubgames = roundSubgamesCache.get(cacheKey);
    if (cachedSubgames) {
      setRoundSubgames(cachedSubgames);
      return;
    }

    let cancelled = false;
    const fetchRoundSubgames = async () => {
      try {
        let request = roundSubgamesRequests.get(cacheKey);
        if (!request) {
          request = fetch(withAccess(`${apiBaseUrl}/api/rounds/${roundId}/subgames`), {
            headers: accessHeaders,
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load round subgames (${response.status})`);
            }
            return normalizeRoundSubgames(await response.json());
          });
          roundSubgamesRequests.set(cacheKey, request);
        }

        const entries = await request;
        roundSubgamesCache.set(cacheKey, entries);
        patchRoundCache(userId, roundId, { roundSubgames: entries });
        if (!cancelled) {
          setRoundSubgames(entries);
        }
      } catch {
        const cached = await readRoundCache(userId, roundId).catch(() => null);
        if (!cancelled) {
          setRoundSubgames(cached?.roundSubgames ?? []);
        }
      } finally {
        roundSubgamesRequests.delete(cacheKey);
      }
    };

    void fetchRoundSubgames();
    return () => {
      cancelled = true;
    };
  }, [skipRestFetches, withAccess, accessHeaders, inviteToken, apiBaseUrl, isNavigationReady, roundId, roundIdIsValid, setRoundSubgames, userId, userToken]);

  useEffect(() => {
    if (!roundId || !isNavigationReady || !roundIdIsValid) {
      setHoleEvents([]);
      return;
    }
    if (skipRestFetches) {
      setHoleEvents([]);
      return;
    }
    if (!userToken && !inviteToken) {
      setHoleEvents([]);
      return;
    }

    const cacheKey = buildRoundCacheKey(apiBaseUrl, roundId, userId);
    const cachedEvents = roundHoleEventsCache.get(cacheKey);
    if (cachedEvents) {
      setHoleEvents(cachedEvents);
      return;
    }

    let cancelled = false;
    const fetchHoleEvents = async () => {
      try {
        let request = roundHoleEventsRequests.get(cacheKey);
        if (!request) {
          request = fetch(withAccess(`${apiBaseUrl}/api/rounds/${roundId}/hole-events`), {
            headers: accessHeaders,
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load hole events (${response.status})`);
            }
            return normalizeHoleEvents(await response.json());
          });
          roundHoleEventsRequests.set(cacheKey, request);
        }

        const events = await request;
        cacheRoundHoleEventsSnapshot(apiBaseUrl, roundId, userId, events);
        if (!cancelled) {
          setHoleEvents(events);
        }
      } catch {
        // Offline fallback: last-known events plus any queued offline writes,
        // so hazards and mystery winners entered offline stay visible.
        // The subject key must match the writer's (user / claim / anonymous),
        // not a hardcoded user:<id> shape.
        const cached = await readRoundCache(userId, roundId).catch(() => null);
        const pending = await loadOfflineQueue(getOfflineSubjectKey(user)).catch(() => []);
        if (!cancelled) {
          const overlaid = applyPendingToSnapshot(
            pending.filter((entry) => entry.roundId === roundId),
            { holeEntries: [], holeEvents: cached?.holeEvents ?? [] }
          );
          setHoleEvents(overlaid.holeEvents);
        }
      } finally {
        roundHoleEventsRequests.delete(cacheKey);
      }
    };

    void fetchHoleEvents();
    return () => {
      cancelled = true;
    };
  }, [skipRestFetches, withAccess, accessHeaders, inviteToken, apiBaseUrl, isNavigationReady, roundId, roundIdIsValid, setHoleEvents, userId, userToken]);

  useEffect(() => {
    if (!isNavigationReady) {
      setSubgames([]);
      return;
    }
    if (skipRestFetches) {
      setSubgames([]);
      return;
    }
    if (!userToken && !inviteToken) {
      setSubgames([]);
      return;
    }

    const cacheKey = buildUserCacheKey(apiBaseUrl, userId);
    const cachedSubgames = subgamesCache.get(cacheKey);
    if (cachedSubgames) {
      setSubgames(cachedSubgames);
      return;
    }

    let cancelled = false;
    const fetchSubgameDefinitions = async () => {
      try {
        let request = subgamesRequests.get(cacheKey);
        if (!request) {
          request = fetch(withAccess(`${apiBaseUrl}/api/subgames`), {
            headers: accessHeaders,
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load subgames (${response.status})`);
            }
            return normalizeSubgameDefinitions(await response.json());
          });
          subgamesRequests.set(cacheKey, request);
        }

        const entries = await request;
        subgamesCache.set(cacheKey, entries);
        patchUserCache(userId, { subgames: entries });
        if (!cancelled) {
          setSubgames(entries);
        }
      } catch {
        const cached = await readUserCache(userId).catch(() => null);
        if (!cancelled) {
          setSubgames(cached?.subgames ?? []);
        }
      } finally {
        subgamesRequests.delete(cacheKey);
      }
    };

    void fetchSubgameDefinitions();
    return () => {
      cancelled = true;
    };
  }, [skipRestFetches, withAccess, accessHeaders, inviteToken, apiBaseUrl, isNavigationReady, setSubgames, userId, userToken]);

  useEffect(() => {
    if (!roundId || !isNavigationReady) {
      setAllHoleEntries([]);
      return;
    }
    if (skipRestFetches) {
      setAllHoleEntries([]);
      return;
    }
    if (!userToken && !inviteToken) {
      setAllHoleEntries([]);
      return;
    }

    const cacheKey = buildRoundCacheKey(apiBaseUrl, roundId, userId);
    const cachedEntries = roundHoleEntriesCache.get(cacheKey);
    if (cachedEntries) {
      setAllHoleEntries(cachedEntries);
      return;
    }

    let cancelled = false;
    const fetchAllHoleEntries = async () => {
      try {
        let request = roundHoleEntriesRequests.get(cacheKey);
        if (!request) {
          request = fetch(withAccess(`${apiBaseUrl}/api/rounds/${roundId}/hole-entries`), {
            headers: accessHeaders,
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load hole entries (${response.status})`);
            }
            const payload = (await response.json()) as Array<Record<string, unknown>>;
            return payload
              .map((entry) => normalizeHoleEntry(entry))
              .filter((entry) => entry.holeNumber > 0);
          });
          roundHoleEntriesRequests.set(cacheKey, request);
        }

        const normalized = await request;
        cacheRoundHoleEntriesSnapshot(apiBaseUrl, roundId, userId, normalized);
        if (!cancelled) {
          setAllHoleEntries(normalized);
        }
      } catch {
        // Offline fallback: last-known entries plus queued offline scores.
        // Same subject key as the writer (user / claim / anonymous).
        const cached = await readRoundCache(userId, roundId).catch(() => null);
        const pending = await loadOfflineQueue(getOfflineSubjectKey(user)).catch(() => []);
        const overlaid = applyPendingToSnapshot(
          pending.filter((entry) => entry.roundId === roundId),
          { holeEntries: cached?.holeEntries ?? [], holeEvents: [] }
        );
        if (!cancelled && overlaid.holeEntries.length > 0) {
          setAllHoleEntries(overlaid.holeEntries);
        }
      } finally {
        roundHoleEntriesRequests.delete(cacheKey);
      }
    };

    void fetchAllHoleEntries();
    return () => {
      cancelled = true;
    };
  }, [skipRestFetches, withAccess, accessHeaders, inviteToken, apiBaseUrl, isNavigationReady, roundId, setAllHoleEntries, userId, userToken]);
};
